import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import BottomSheet from "@/components/BottomSheet";
import { formatNominalInput, parseNominal, todayYMD } from "@/lib/format";

export default function HutangForm({ open, onClose, onSaved, existing = null, rekeningList = [] }) {
  const [deskripsi, setDeskripsi] = useState(existing?.deskripsi || "");
  const [total, setTotal] = useState(existing?.total ? formatNominalInput(existing.total) : "");
  const [tanggalMulai, setTanggalMulai] = useState(existing?.tanggal_mulai || todayYMD());
  const [jatuhTempo, setJatuhTempo] = useState(existing?.jatuh_tempo || "");
  const [dariRekening, setDariRekening] = useState(false);
  const [rekeningAsalId, setRekeningAsalId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setDeskripsi(existing?.deskripsi || "");
      setTotal(existing?.total ? formatNominalInput(existing.total) : "");
      setTanggalMulai(existing?.tanggal_mulai || todayYMD());
      setJatuhTempo(existing?.jatuh_tempo || "");
      setDariRekening(false);
      setRekeningAsalId("");
    }
  }, [open, existing]);

  const submit = async (e) => {
    e.preventDefault();
    const n = parseNominal(total);
    if (!deskripsi.trim()) return toast.error("Deskripsi wajib diisi");
    if (n <= 0) return toast.error("Total harus lebih dari 0");
    if (!jatuhTempo) return toast.error("Jatuh tempo wajib diisi");
    if (dariRekening && !rekeningAsalId) return toast.error("Pilih rekening asal");
    setBusy(true);
    try {
      const payload = {
        deskripsi: deskripsi.trim(),
        total: n,
        tanggal_mulai: tanggalMulai,
        jatuh_tempo: jatuhTempo,
        rekening_asal_id: dariRekening ? rekeningAsalId : null,
      };
      if (existing) {
        await api.put(`/hutang/${existing.id}`, payload);
        toast.success("Hutang diperbarui");
      } else {
        await api.post("/hutang", payload);
        toast.success("Hutang dicatat");
      }
      onClose();
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={existing ? "Edit Hutang" : "Catat Hutang"}
      testId="hutang-form-sheet"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="hutang-form">
        <Field label="Tanggal mulai">
          <input
            type="date"
            value={tanggalMulai}
            onChange={(e) => setTanggalMulai(e.target.value)}
            className={inputCls}
            required
          />
        </Field>
        <Field label="Nama / Deskripsi">
          <input
            type="text"
            value={deskripsi}
            onChange={(e) => setDeskripsi(e.target.value)}
            className={inputCls}
            placeholder="mis. Pinjam untuk Papa, Pinjam beli HP"
            required
          />
        </Field>
        <Field label="Total (Rp)">
          <input
            type="text"
            inputMode="numeric"
            value={total}
            onChange={(e) => setTotal(formatNominalInput(e.target.value))}
            className={inputCls}
            required
          />
        </Field>
        <Field label="Jatuh tempo">
          <input
            type="date"
            value={jatuhTempo}
            onChange={(e) => setJatuhTempo(e.target.value)}
            className={inputCls}
            required
          />
        </Field>

        {/* Toggle Dari Rekening Sendiri */}
        {!existing && (
          <div
            className="rounded-xl p-3 border"
            style={{ borderColor: dariRekening ? "#2563EB" : "#E5E7EB", background: dariRekening ? "#EFF6FF" : "#F9FAFB" }}
          >
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <div className="text-xs font-semibold" style={{ color: "#111827" }}>
                  Dari rekening sendiri
                </div>
                <div className="text-[10px] mt-0.5" style={{ color: "#6B7280" }}>
                  Aktifkan jika pinjam dari tabungan/rekening sendiri
                </div>
              </div>
              <div
                onClick={() => setDariRekening(!dariRekening)}
                className="w-11 h-6 rounded-full relative cursor-pointer transition-all"
                style={{ background: dariRekening ? "#2563EB" : "#D1D5DB" }}
              >
                <div
                  className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all"
                  style={{ left: dariRekening ? "22px" : "2px" }}
                />
              </div>
            </label>

            {dariRekening && (
              <div className="mt-3">
                <div className="text-xs font-semibold text-slate-600 mb-1">Pilih rekening asal</div>
                <select
                  value={rekeningAsalId}
                  onChange={(e) => setRekeningAsalId(e.target.value)}
                  className={inputCls}
                  required={dariRekening}
                >
                  <option disabled>Total rekening: {rekeningList.length}</option>
                  <option value="">— Pilih rekening —</option>
                  {rekeningList.length === 0 && <option disabled>Tidak ada rekening</option>}
                  {rekeningList.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.nama} ({r.bank})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

{/* Info rekening asal saat edit */}
{existing && existing.rekening_asal_id && (
  <div
    className="rounded-xl p-3 border"
    style={{ borderColor: "#2563EB", background: "#EFF6FF" }}
  >
    <div className="text-xs font-semibold mb-1" style={{ color: "#111827" }}>
      Hutang dari rekening sendiri
    </div>
    <div className="text-xs" style={{ color: "#2563EB" }}>
      {rekeningList.find(r => r.id === existing.rekening_asal_id)?.nama || "Rekening tidak ditemukan"}
    </div>
  </div>
)}
        <button
          type="submit"
          disabled={busy}
          className="w-full h-11 rounded-xl font-semibold text-sm text-white disabled:opacity-60"
          style={{ backgroundColor: "#1F4E5F" }}
        >
          {busy ? "Menyimpan..." : "Simpan"}
        </button>
      </form>
    </BottomSheet>
  );
}

const inputCls = "mt-1 w-full h-11 px-3 rounded-xl border border-slate-200 focus:border-[#1F4E5F] focus:outline-none text-sm bg-white";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      {children}
    </label>
  );
}